import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Clock, Plus, Check, X, Filter, AlertCircle, Users, DollarSign, Calendar, Eye, TrendingUp, Pencil, Trash2, Info, Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { OT_TYPE_LABELS, OvertimePreviewCard, OvertimeRateTable, useOvertimePreview } from '../components/overtime/OvertimePreview';

export default function OvertimeManagement() {
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  
  const [formData, setFormData] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '18:00',
    end_time: '20:00',
    overtime_type: 'normal_day',
    reason: ''
  });
  const [editForm, setEditForm] = useState({
    employee_id: '',
    date: '',
    start_time: '',
    end_time: '',
    overtime_type: 'normal_day',
    reason: ''
  });
  const [editSaving, setEditSaving] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const canReview = ['admin', 'hr_manager', 'department_head', 'manager'].includes(user.role);
  const canViewAll = ['admin', 'hr_manager', 'hr_user', 'department_head', 'manager'].includes(user.role);

  // Day type (normal / off day / rest day / public holiday) is classified by
  // the backend per employee: public holiday > designated rest day > off day.
  const createPreview = useOvertimePreview(formData, dialogOpen);
  const editPreview = useOvertimePreview(editForm, editDialogOpen);
  useEffect(() => {
    if (createPreview?.day_type && createPreview.day_type !== formData.overtime_type) setFormData((f) => ({ ...f, overtime_type: createPreview.day_type }));
  }, [createPreview]);
  useEffect(() => {
    if (editPreview?.day_type && editPreview.day_type !== editForm.overtime_type) setEditForm((f) => ({ ...f, overtime_type: editPreview.day_type }));
  }, [editPreview]);

  useEffect(() => {
    fetchData();
  }, [filterMonth, filterStatus]);

  const fetchData = async () => {
    try {
      const [reqRes, empRes] = await Promise.all([
        axios.get(`${API}/overtime/requests?month=${filterMonth}${filterStatus !== 'all' ? `&status=${filterStatus}` : ''}`),
        axios.get(`${API}/employees`)
      ]);
      setRequests(reqRes.data);
      setEmployees(empRes.data);

      if (canViewAll) {
        const summaryRes = await axios.get(`${API}/overtime/summary?month=${filterMonth}`);
        setSummary(summaryRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch overtime data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id || !formData.date || !formData.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/overtime/request`, formData);
      toast.success('Overtime request submitted');
      setDialogOpen(false);
      setFormData({
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '18:00',
        end_time: '20:00',
        overtime_type: 'normal_day',
        reason: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit overtime request');
    }
  };

  const handleReview = async () => {
    if (!selectedRequest || !reviewAction) return;
    
    if (reviewAction === 'rejected' && !reviewRemarks.trim()) {
      toast.error('Please provide remarks for rejection');
      return;
    }
    
    try {
      const params = new URLSearchParams({ action: reviewAction });
      if (reviewRemarks.trim()) {
        params.append('remarks', reviewRemarks.trim());
      }
      
      await axios.put(`${API}/overtime/requests/${selectedRequest.overtime_id}/review?${params.toString()}`);
      toast.success(`Overtime ${reviewAction}`);
      setReviewDialogOpen(false);
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review overtime');
    }
  };

  const openEditDialog = (req) => {
    setSelectedRequest(req);
    setEditForm({
      employee_id: req.employee_id,
      date: req.date?.slice(0, 10) || '',
      start_time: req.start_time,
      end_time: req.end_time,
      overtime_type: req.overtime_type,
      reason: req.reason || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRequest?.overtime_id) return;
    if (!editForm.date || !editForm.start_time || !editForm.end_time || !editForm.reason.trim()) {
      toast.error('Please fill all required fields');
      return;
    }
    setEditSaving(true);
    try {
      const res = await axios.put(`${API}/overtime/requests/${selectedRequest.overtime_id}`, editForm);
      toast.success('Overtime request updated');
      setEditDialogOpen(false);
      // refresh selectedRequest so detail dialog (if open) shows new values
      if (res.data?.overtime) setSelectedRequest(res.data.overtime);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update overtime request');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (req) => {
    if (!window.confirm(`Withdraw this pending overtime request for ${req.employee_name} on ${req.date}?`)) return;
    try {
      await axios.delete(`${API}/overtime/requests/${req.overtime_id}`);
      toast.success('Overtime request withdrawn');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete overtime request');
    }
  };

  // Export the current month's overtime requests (already filtered by Month/Status
  // via the existing `requests` state) to an Excel workbook.
  // - Sheet 1 "Summary": Period + totals (Total Employees, Total Hours, Total Pay,
  //   pending/approved/rejected counts) — same numbers the cards show.
  // - Sheet 2 "Overtime Requests": one row per request with full breakdown.
  // No data recalculation — values come from the already-loaded `requests`.
  const exportOvertimeToExcel = async () => {
    if (!canViewAll) {
      toast.error('Not authorized to export overtime reports');
      return;
    }
    if (!requests || requests.length === 0) {
      toast.error('No overtime requests to export for this period');
      return;
    }
    try {
      const XLSX = await import('xlsx');
      const [yr, mo] = (filterMonth || '').split('-');
      const monthName = mo
        ? new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString('en-US', { month: 'long' })
        : '';
      const periodLabel = mo ? `${monthName} ${yr}` : 'All Months';

      // Totals — mirror the summary cards exactly
      const pendingCount = requests.filter((r) => r.status === 'pending').length;
      const approvedCount = requests.filter((r) => r.status === 'approved').length;
      const rejectedCount = requests.filter((r) => r.status === 'rejected').length;
      const totalHours = (summary && typeof summary.total_hours === 'number')
        ? summary.total_hours
        : Number(requests.reduce((s, r) => s + (Number(r.hours) || 0), 0).toFixed(2));
      const totalPay = (summary && typeof summary.total_pay === 'number')
        ? summary.total_pay
        : Number(requests.reduce((s, r) => s + (Number(r.overtime_pay) || 0), 0).toFixed(2));
      const totalEmployees = (summary && typeof summary.total_employees === 'number')
        ? summary.total_employees
        : new Set(requests.map((r) => r.employee_id)).size;

      const summaryAoa = [
        ['Overtime Monthly Report'],
        ['Period', periodLabel],
        ['Status Filter', filterStatus === 'all' ? 'All' : filterStatus],
        [],
        ['Total Employees', totalEmployees],
        ['Total Hours', totalHours],
        ['Total Pay (RM)', Number(totalPay).toFixed(2)],
        ['Pending', pendingCount],
        ['Approved', approvedCount],
        ['Rejected', rejectedCount],
      ];
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryAoa);
      summaryWs['!cols'] = [{ wch: 24 }, { wch: 22 }];

      // Detailed rows
      const rows = requests.map((r) => ({
        'Employee Name': r.employee_name || '',
        'Employee ID': r.employee_id || '',
        'Date': (r.date || '').slice(0, 10),
        'Start Time': formatTime12h(r.start_time),
        'End Time': formatTime12h(r.end_time),
        'Hours': Number(r.hours || 0),
        'OT Type': getOTTypeLabel(r.overtime_type).label,
        'Multiplier': Number(r.rate_multiplier || 0),
        'Hourly Rate (RM)': Number(r.hourly_rate || 0).toFixed(2),
        'OT Pay (RM)': Number(r.overtime_pay || 0).toFixed(2),
        'Status': r.status || '',
        'Reason': r.reason || '',
      }));

      // TOTAL row using same totals as cards
      rows.push({
        'Employee Name': 'TOTAL',
        'Employee ID': '',
        'Date': '',
        'Start Time': '',
        'End Time': '',
        'Hours': totalHours,
        'OT Type': '',
        'Multiplier': '',
        'Hourly Rate (RM)': '',
        'OT Pay (RM)': Number(totalPay).toFixed(2),
        'Status': '',
        'Reason': '',
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 11 }, { wch: 11 },
        { wch: 8 },  { wch: 22 }, { wch: 10 }, { wch: 14 }, { wch: 12 },
        { wch: 10 }, { wch: 32 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      XLSX.utils.book_append_sheet(wb, ws, 'Overtime Requests');

      const fname = mo
        ? `overtime-report-${monthName}-${yr}.xlsx`
        : `overtime-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success(`Exported ${fname}`);
    } catch (err) {
      toast.error('Excel export failed');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'approved': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      'rejected': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return styles[status] || styles['pending'];
  };

  const getOTTypeLabel = (type) => OT_TYPE_LABELS[type] || OT_TYPE_LABELS.normal_day;

  // Format a 24-hour "HH:MM" string into "h:mm AM/PM" so the table matches
  // the browser-native <input type="time"> rendering used in the New OT
  // Request form (which displays AM/PM on most browser locales).
  const formatTime12h = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return hhmm || '';
    const [hStr, mStr] = hhmm.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1; // 0->12, 13->1, 23->11
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Overtime Management</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Malaysia Employment Act 1955 - Max 104 hours/month
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canViewAll && (
            <Button
              type="button"
              variant="outline"
              onClick={exportOvertimeToExcel}
              disabled={!requests || requests.length === 0}
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-200 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40 disabled:opacity-50"
              data-testid="export-overtime-excel-btn"
              title={(!requests || requests.length === 0) ? 'No overtime requests in this period to export' : 'Export the current month/status filter to Excel'}
            >
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          )}
          <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            New OT Request
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && canViewAll && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center space-x-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Employees</span>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.total_employees}</p>
          </div>
          
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center space-x-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Clock className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Hours</span>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.total_hours}h</p>
          </div>
          
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center space-x-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Pay</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">RM {summary.total_pay.toLocaleString()}</p>
          </div>
          
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center space-x-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Pending</span>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {requests.filter(r => r.status === 'pending').length}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
        <Filter className="h-5 w-5 text-slate-500" />
        <div className="flex items-center space-x-2">
          <Label className="text-sm">Month:</Label>
          <Input
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="w-40 h-9 dark:bg-slate-900"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Label className="text-sm">Status:</Label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-9 rounded-lg border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* OT Rate Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4" data-testid="ot-rate-info">
        <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
          <Info className="h-4 w-4" />
          Malaysia Employment Act 1955 — Overtime Rates
        </h3>
        <OvertimeRateTable />
        <div className="text-xs text-blue-700 dark:text-blue-300 border-t border-blue-200 dark:border-blue-800 pt-2 mt-3" data-testid="hourly-rate-formula">
          <strong>ORP</strong> (Ordinary Rate of Pay) = Basic Salary ÷ 26 · <strong>HRP</strong> (Hourly Rate of Pay) = ORP ÷ 8 = Basic Salary ÷ 208.
          <span className="ml-1 opacity-80">Normal working hours are 8 per day. Sunday is only a Rest Day when it is the employee's designated statutory rest day (Employee → Rest Day, else Company Settings default).</span>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Overtime Requests</h3>
        </div>
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No overtime requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">OT Pay</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {requests.map(req => {
                  const otType = getOTTypeLabel(req.overtime_type);
                  return (
                    <tr key={req.overtime_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{req.employee_name}</p>
                        <p className="text-xs text-slate-500">{req.employee_id}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {new Date(req.date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {formatTime12h(req.start_time)} - {formatTime12h(req.end_time)}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {req.hours}h
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm font-medium ${otType.color}`}>{otType.label}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="font-semibold text-emerald-600">RM {req.overtime_pay?.toFixed(2)}</span>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {req.calculation || `${req.hours}h × RM ${Number(req.hourly_rate || 0).toFixed(2)} × ${Number(req.rate_multiplier || 0).toFixed(1)}`}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(req.status)}`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => { setSelectedRequest(req); setDetailDialogOpen(true); }}
                            className="h-8 border-slate-300 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700/40"
                            data-testid={`ot-view-btn-${req.overtime_id}`}
                          >
                            <Eye className="h-3 w-3 mr-1" />View
                          </Button>
                          {req.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(req)}
                              className="h-8 border-indigo-400 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-500 dark:text-indigo-200 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/40"
                              data-testid={`ot-edit-btn-${req.overtime_id}`}
                            >
                              <Pencil className="h-3 w-3 mr-1" />Edit
                            </Button>
                          )}
                          {canReview && req.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => { setSelectedRequest(req); setReviewAction('approved'); setReviewDialogOpen(true); }}
                                className="bg-emerald-600 hover:bg-emerald-700 h-8 text-white"
                                data-testid={`ot-approve-btn-${req.overtime_id}`}
                              >
                                <Check className="h-3 w-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => { setSelectedRequest(req); setReviewAction('rejected'); setReviewRemarks(''); setReviewDialogOpen(true); }}
                                className="h-8"
                                data-testid={`ot-reject-btn-${req.overtime_id}`}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          {req.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(req)}
                              className="h-8 border-red-400 text-red-700 hover:bg-red-50 dark:border-red-500 dark:text-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/40"
                              data-testid={`ot-withdraw-btn-${req.overtime_id}`}
                              title="Withdraw"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New OT Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">New Overtime Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="dark:text-slate-100">Employee *</Label>
              <select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
                required
                data-testid="ot-create-employee-select"
              >
                <option value="">Select Employee</option>
                {employees.filter(e => e.status !== 'terminated').map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="dark:text-slate-100">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => {
                  setFormData({ ...formData, date: e.target.value });
                }}
                className="dark:bg-slate-900"
                required
                max={new Date().toISOString().slice(0, 10)}
                data-testid="ot-create-date-input"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Overtime is logged for work already performed — future dates are not allowed.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-slate-100">Start Time *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="dark:bg-slate-900"
                  required
                  data-testid="ot-create-start-input"
                />
              </div>
              <div>
                <Label className="dark:text-slate-100">End Time *</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="dark:bg-slate-900"
                  required
                  data-testid="ot-create-end-input"
                />
              </div>
            </div>
            <div>
              <Label className="dark:text-slate-100">Day Type (auto-classified) *</Label>
              <select
                value={formData.overtime_type}
                onChange={(e) => setFormData({ ...formData, overtime_type: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100 disabled:opacity-70 disabled:cursor-not-allowed"
                required
                disabled
                data-testid="ot-create-type-select"
              >
                <option value="normal_day">Normal Working Day (1.5× HRP)</option>
                <option value="off_day">Off Day (1.5× HRP)</option>
                <option value="rest_day">Rest Day (0.5 / 1.0 × ORP, 2.0 × HRP beyond 8h)</option>
                <option value="public_holiday">Public Holiday (2.0 × ORP, 3.0 × HRP beyond 8h)</option>
              </select>
              <div className="mt-2"><OvertimePreviewCard preview={createPreview} testId="ot-create-preview" /></div>
            </div>
            <div>
              <Label className="dark:text-slate-100">Reason *</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Reason for overtime..."
                className="dark:bg-slate-900"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700">Submit</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Pending OT Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800" data-testid="ot-edit-dialog">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Edit Pending Overtime Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label className="dark:text-slate-100">Employee</Label>
              <select
                value={editForm.employee_id}
                onChange={(e) => setEditForm({ ...editForm, employee_id: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100"
                disabled={!canReview}
                data-testid="ot-edit-employee-select"
              >
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name}</option>
                ))}
              </select>
              {!canReview && (
                <p className="text-xs text-slate-500 mt-1">Only HR / managers can reassign the employee.</p>
              )}
            </div>
            <div>
              <Label className="dark:text-slate-100">Date *</Label>
              <Input
                type="date"
                value={editForm.date}
                onChange={(e) => {
                  setEditForm({ ...editForm, date: e.target.value });
                }}
                className="dark:bg-slate-900"
                required
                max={new Date().toISOString().slice(0, 10)}
                data-testid="ot-edit-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-slate-100">Start Time *</Label>
                <Input
                  type="time"
                  value={editForm.start_time}
                  onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                  className="dark:bg-slate-900"
                  required
                  data-testid="ot-edit-start-input"
                />
              </div>
              <div>
                <Label className="dark:text-slate-100">End Time *</Label>
                <Input
                  type="time"
                  value={editForm.end_time}
                  onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                  className="dark:bg-slate-900"
                  required
                  data-testid="ot-edit-end-input"
                />
              </div>
            </div>
            <div>
              <Label className="dark:text-slate-100">Day Type (auto-classified) *</Label>
              <select
                value={editForm.overtime_type}
                onChange={(e) => setEditForm({ ...editForm, overtime_type: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 dark:bg-slate-900 dark:text-slate-100 disabled:opacity-70 disabled:cursor-not-allowed"
                required
                disabled
                data-testid="ot-edit-type-select"
              >
                <option value="normal_day">Normal Working Day (1.5× HRP)</option>
                <option value="off_day">Off Day (1.5× HRP)</option>
                <option value="rest_day">Rest Day (0.5 / 1.0 × ORP, 2.0 × HRP beyond 8h)</option>
                <option value="public_holiday">Public Holiday (2.0 × ORP, 3.0 × HRP beyond 8h)</option>
              </select>
              <div className="mt-2"><OvertimePreviewCard preview={editPreview} testId="ot-edit-preview" /></div>
            </div>
            <div>
              <Label className="dark:text-slate-100">Reason *</Label>
              <Textarea
                value={editForm.reason}
                onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                placeholder="Reason for overtime..."
                className="dark:bg-slate-900"
                required
                data-testid="ot-edit-reason-input"
              />
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 rounded p-2">
              OT Pay will be recalculated automatically using the employee&apos;s current basic salary and the new hours.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editSaving}>Cancel</Button>
              <Button
                type="submit"
                disabled={editSaving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="ot-edit-save-btn"
              >
                {editSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Overtime Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Employee</Label>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{selectedRequest.employee_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Date</Label>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {new Date(selectedRequest.date).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Time</Label>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{formatTime12h(selectedRequest.start_time)} - {formatTime12h(selectedRequest.end_time)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Hours</Label>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{selectedRequest.hours} hours</p>
                </div>
                <div>
                  <Label className="text-slate-500">Type</Label>
                  <p className={`font-medium ${getOTTypeLabel(selectedRequest.overtime_type).color}`}>
                    {getOTTypeLabel(selectedRequest.overtime_type).label}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Hourly Rate</Label>
                  <p className="font-medium text-slate-900 dark:text-slate-100">RM {selectedRequest.hourly_rate?.toFixed(2)}</p>
                </div>
              </div>
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <Label className="text-emerald-700 dark:text-emerald-400">Overtime Pay</Label>
                <p className="text-2xl font-bold text-emerald-600">RM {selectedRequest.overtime_pay?.toFixed(2)}</p>
                <p className="text-xs text-emerald-600 mt-1">
                  {selectedRequest.calculation || `${selectedRequest.hours}h × RM ${selectedRequest.hourly_rate?.toFixed(2)} × ${selectedRequest.rate_multiplier}`}
                </p>
                {selectedRequest.day_reason && <p className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80 mt-1">{selectedRequest.day_reason}</p>}
              </div>
              <div>
                <Label className="text-slate-500">Reason</Label>
                <p className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg text-slate-900 dark:text-slate-100">{selectedRequest.reason}</p>
              </div>
              {selectedRequest.rejection_remarks && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <Label className="text-red-700">Rejection Remarks</Label>
                  <p className="text-red-600">{selectedRequest.rejection_remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">
              {reviewAction === 'approved' ? 'Approve' : 'Reject'} Overtime
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedRequest && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-sm"><strong>Employee:</strong> {selectedRequest.employee_name}</p>
                <p className="text-sm"><strong>Hours:</strong> {selectedRequest.hours}h ({getOTTypeLabel(selectedRequest.overtime_type).label})</p>
                <p className="text-sm"><strong>OT Pay:</strong> RM {selectedRequest.overtime_pay?.toFixed(2)}</p>
              </div>
            )}
            {reviewAction === 'rejected' && (
              <div>
                <Label className="dark:text-slate-100">Rejection Remarks *</Label>
                <Textarea
                  value={reviewRemarks}
                  onChange={(e) => setReviewRemarks(e.target.value)}
                  placeholder="Please provide reason for rejection..."
                  className="dark:bg-slate-900"
                  required
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleReview}
                className={reviewAction === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {reviewAction === 'approved' ? 'Approve' : 'Reject'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
